<template>
    <ion-page>
        <ion-content>
            <!-- <IonRefresher
                check if needed
                @ionRefresh="lifecycleStore.refresh"
                slot="fixed"
            >
                <IonRefresherContent></IonRefresherContent>
            </IonRefresher> -->
            <BaseMap
                class="h-full w-full"
                :show-draw="false"
                :settings="mapSettings"
                :layers="mapLayers"
                :sources="mapSources"
                :popups="popups"
                :popup-layers="popupLayers"
                @on-first-idle="onMapLoad"
            >
                <template
                    v-if="activityStore.isActivityRunning"
                    #bottom
                >
                    <ActivityBanner
                        v-if="activityStore.showActivityRunningBanner"
                        class="w-full mx-2 mb-4 max-w-64"
                        :text="$t('activity.running')"
                        :button-text="$t('activity.pause')"
                        :icon="caretForwardCircleOutline"
                        iconColor="green"
                        @click="activityStore.isActivityRunning = false"
                    />
                    <ActivityBanner
                        v-if="activityStore.showInsideFenceBanner"
                        class="w-full mx-2 mb-4 max-w-64"
                        :text="$t('activity.insideFence')"
                        :icon="caretForwardCircleOutline"
                        iconColor="red"
                    />
                    <ActivityBanner
                        v-if="activityStore.showInaccuracyBanner"
                        class="inaccuracy-banner w-full mx-2 mb-4 max-w-64"
                        :text="$t('activity.inaccurate')"
                        :icon="ellipseOutline"
                        iconColor="blue"
                    />
                </template>
            </BaseMap>
            <span ref="fencePopupElement">
                <div v-if="showFencePopup">
                    <p>{{ infoFenceName }}</p>
                </div>
            </span>
            <span ref="positionCirclePopupElement">
                <div v-if="showPositionCirclePopup">
                    <p v-if="infoRadius">{{ $t('map.popups.radius') }}: {{ infoRadius }}m</p>
                    <p v-if="infoInaccuracy">{{ $t('map.popups.inaccuracy') }}: {{ infoInaccuracy }}m</p>
                </div>
            </span>
        </ion-content>
    </ion-page>
</template>

<script
    setup
    lang="ts"
>
import useHeader from '@/composables/useHeader';
import { useActivityStore } from '@/store/activity';
import { useFenceStore } from '@/store/fence';
import { useNotificationsStore } from '@/store/notification';
import {
    mapLayerFences,
    mapLayerPositionCircle,
    mapLayerPositionCircleFill,
    mapLayerPositionCircleFillInsideFence,
    mapLayerPositionCircleInsideFence,
    sourceIdFences,
    sourcePositionCircle
} from '@/types/map-config';
import { IonContent, IonPage } from '@ionic/vue';
import { caretForwardCircleOutline, ellipseOutline } from "ionicons/icons";
import { LayerSpecification, MapMouseEvent, MapTouchEvent } from 'maplibre-gl';
import { BaseMap, emptyGeoJSON, FeatureCollection, getGeoJSONWrapperForFeatureCollection, PopupDefinition } from '@movinglayers/map';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ActivityBanner from '../elements/ActivityBanner.vue';

const { t } = useI18n();
const fenceStore = useFenceStore();
const activityStore = useActivityStore();
const notificationStore = useNotificationsStore();
const isMapLoaded = ref(false);

const showFencePopup = ref(false);
const showPositionCirclePopup = ref(false);
const fencePopupElement = ref();
const positionCirclePopupElement = ref();
const popups = ref<PopupDefinition[]>([]);

const infoFenceName = ref("");
const infoRadius = ref(0);
const infoInaccuracy = ref(0);

const mapSettings = ref({
    style: "https://static.maptoolkit.net/styles/movinglayers/Virtual_Shepherd.json?api_key=movinglayers"
});
const mapSources = ref<Record<string, {}>>({
    [sourceIdFences]: emptyGeoJSON(t("map.layer.fences")),
    [sourcePositionCircle]: emptyGeoJSON(t("map.layer.position"))
});
const mapLayers = ref<LayerSpecification[]>([
    mapLayerFences,
    mapLayerPositionCircle,
    mapLayerPositionCircleFill,
    mapLayerPositionCircleFillInsideFence,
    mapLayerPositionCircleInsideFence
]);

useHeader({
    title: t("header.map"),
    showBackButton: false,
    showConfirmButton: false
});

const popupLayers = computed(() => {
    // @ts-ignore Type instantiation is excessively deep and possibly infinite.
    return mapLayers.value.map(layer => layer.id);
});

const setSources = (featureCollection: FeatureCollection) => {
    mapSources.value[sourceIdFences] = getGeoJSONWrapperForFeatureCollection(t("map.layer.fences"), featureCollection);
};

const setPopups = () => {
    popups.value = [
        {
            element: fencePopupElement.value,
            onShow: (event: MapMouseEvent | MapTouchEvent) => {
                // @ts-ignore features not in MapMouseEvent type, although mapli re docs mention features property multiple times
                const feature = event?.features[0];
                if (feature && feature.layer.id === mapLayerFences.id) {
                    infoFenceName.value = feature.properties?.name;
                    showFencePopup.value = true;
                    return true;
                }
                return false;
            },
            onHide: () => showFencePopup.value = false
        },
        {
            element: positionCirclePopupElement.value,
            onShow: (event: MapMouseEvent | MapTouchEvent) => {
                // @ts-ignore probably because of LayerSpecification type: Type instantiation is excessively deep and possibly infinite. 
                const layers = mapLayers.value.filter((layer) => layer.id !== mapLayerFences.id);
                // @ts-ignore features not in MapMouseEvent type, although maplibre docs mention features property multiple times
                const clickedLayer = event?.features?.[0]?.layer;
                if (clickedLayer && layers.some((layer) => layer.id === clickedLayer.id)) {
                    showPositionCirclePopup.value = true;
                    infoRadius.value = activityStore.currentRadius ?? null;
                    infoInaccuracy.value = notificationStore.currentPosition?.[2] ?? null;
                    return true;
                }
                return false;
            },
            onHide: () => showPositionCirclePopup.value = false
        }
    ];
};

const onMapLoad = () => {
    setSources(fenceStore.fenceFeatureCollection);
    isMapLoaded.value = true;
};

watch(() => fenceStore.fenceFeatureCollection, (featureCollection: FeatureCollection) => {
    if (isMapLoaded.value) {
        setSources(featureCollection);
    }
});

watch(() => notificationStore.currentPositionCircleFeatureCollection, (featureCollection: FeatureCollection) => {
    if (isMapLoaded.value) {
        mapSources.value[sourcePositionCircle] = getGeoJSONWrapperForFeatureCollection(t("map.layer.position"), featureCollection);
    }
});

watch(() => activityStore.currentRadius, (radius) => {
    infoRadius.value = radius;
});

watch(() => notificationStore.currentPosition, (position) => {
    infoInaccuracy.value = position?.[2] ?? null;
});

onMounted(() => {
    setPopups();
});

</script>